import { getConfig } from '@edx/frontend-platform';
import { getAuthenticatedHttpClient } from '@edx/frontend-platform/auth';

/**
 * Fetches the server-filtered, paginated list of a learner's ongoing courses.
 * Backed by GET /blendxapi/learner/progress/ongoing-courses/.
 *
 * @param {Object} [params]
 * @param {string} [params.search] Case-insensitive substring match against course title.
 * @param {('all'|'not_started'|'in_progress'|'completed')} [params.status]
 * @param {number} [params.limit] Page size (max 100).
 * @param {number} [params.offset] Pagination offset.
 * @returns {Promise<{courses: Array, pagination: {total: number, limit: number, offset: number}}>}
 */
export const fetchOngoingCoursesList = async ({
  search = '',
  status = 'all',
  limit = 20,
  offset = 0,
} = {}) => {
  const url = new URL(`${getConfig().LMS_BASE_URL}/blendxapi/learner/progress/ongoing-courses/`);
  if (search) url.searchParams.set('search', search);
  if (status && status !== 'all') url.searchParams.set('status', status);
  url.searchParams.set('limit', String(limit));
  url.searchParams.set('offset', String(offset));
  const { data } = await getAuthenticatedHttpClient().get(url.toString());
  return data;
};

/**
 * Fetch the requesting learner's own points / rank across all windows.
 * GET /blendxapi/learner/analytics/my-points/
 * Response: { windows: { week|month|all: { rank, block_points, skill_points,
 *             streak_points, total_points, total_users } } }
 */
export const fetchMyPoints = async () => {
  const url = `${getConfig().LMS_BASE_URL}/blendxapi/learner/analytics/my-points/`;
  const { data } = await getAuthenticatedHttpClient().get(url);
  return data;
};

/**
 * Fetch one page of the org leaderboard (learner-accessible).
 * GET /blendxapi/learner/analytics/leaderboard/
 * Response: { leaderboard: [{ rank, user_id, username, full_name, avatar_url,
 *             block_points, skill_points, streak_points, total_points }],
 *             pagination: { total, page, page_size } }
 *
 * @param {string} window - 'week' | 'month' | 'all'
 * @param {number} page - 1-based page number
 * @param {number} pageSize - rows per page
 */
export const fetchLeaderboard = async (window, page, pageSize) => {
  const url = `${getConfig().LMS_BASE_URL}/blendxapi/learner/analytics/leaderboard/`;
  const { data } = await getAuthenticatedHttpClient().get(url, {
    params: { window, page, page_size: pageSize },
  });
  return data;
};
